import { ColumnDef } from "@tanstack/react-table";
import {
  SectionOption,
  Section,
} from "@components/NestedItemSelect/NestedItemSelect";
import { Project } from "./types/Project";
import { snakeToText } from "@utils/helperFunctions";

export const col = (id: string, header: string): ColumnDef<SectionOption> => ({
  id,
  header,
  cell: (props) => (props.row.original[id] as string) || "",
});

export const buildGeneralSection = (ids: string[]): Section => ({
  id: "general",
  name: "General",
  selected: true,
  options: ids.map((id) => ({ id, name: snakeToText(id), selected: true })),
  columns: [col("name", "General Sections to Include")],
});

export const buildPsSection = (project: Project | null): Section => ({
  id: "professional-services",
  name: "Professional Services",
  selected: true,
  options: (project?.services?.filter(
    (s) => s.serviceType == "professional_services"
  ) ?? []) as SectionOption[],
  columns: [
    col("name", "Professional Services To Include"),
    col("quantity", "QTY"),
    col("hours", "Unit Hrs"),
    col("resource", "Resource"),
  ],
});

export const buildMsSection = (project: Project | null): Section => ({
  id: "managed-services",
  name: "Managed Services",
  selected: true,
  options: (project?.services?.filter(
    (s) => s.serviceType == "managed_services"
  ) ?? []) as SectionOption[],
  columns: [
    col("name", "Managed Services To Include"),
    col("quantity", "QTY"),
    col("price", "Monthly Price"),
    col("cost", "Monthly Cost"),
  ],
});

export const buildExpenseSection = (project: Project | null): Section => ({
  id: "expense",
  name: "Expense",
  selected: true,
  options: (project?.expenses ?? []) as SectionOption[],
  columns: [
    col("name", "Expenses To Include"),
    col("quantity", "Quantity"),
    col("cost", "Cost"),
  ],
});

export const buildVendorQuotesSection = (project: Project | null): Section => ({
  id: "vendor-quotes",
  name: "Vendor Quotes",
  selected: true,
  options: (project?.quotes ?? []) as SectionOption[],
  columns: [
    col("name", "Vendor Quotes To Include"),
    col("cost", "One Time Cost"),
    col("recCost", "Recurring Cost"),
    col("revenue", "One Time Revenue"),
    col("recRevenue", "Recurring Revenue"),
  ],
});

export const buildGovernanceSection = (project: Project | null): Section => ({
  id: "governance",
  name: "Governance",
  selected: true,
  options: (project?.governances ?? []) as SectionOption[],
  columns: [
    col("name", "Governances To Include"),
    col("phase", "Phase"),
    col("resource", "Resource"),
    col("calculation", "Calc Hrs By"),
    col("hours", "Hours"),
  ],
});

export const buildProductSection = (project: Project | null): Section => ({
  id: "product",
  name: "Product",
  selected: true,
  options: (project?.products ?? []) as SectionOption[],
  columns: [
    col("name", "Products To Include"),
    col("mfrPart", "MFR Part"),
    col("quantity", "Quantity"),
    col("units", "Units"),
    col("price", "Unit Price"),
  ],
});

export const buildResourcesSection = (project: Project | null): Section => ({
  id: "resources",
  name: "Resources",
  selected: true,
  options: (project?.resources ?? []) as SectionOption[],
  columns: [
    col("name", "Resources To Include"),
    col("lob", "LOB/Category"),
    col("hourlyRate", "Hourly Rate"),
    col("expenseRate", "Travel/Expense Rate"),
  ],
});

export const buildPricingSection = (ids: string[]): Section => ({
  id: "pricing",
  name: "Pricing",
  selected: true,
  options: ids.map((id) => ({ id, name: snakeToText(id), selected: true })),
  columns: [col("name", "Pricing Options To Include")],
});

export const buildPayload = (
  sections: Section[],
  project: Project | null | undefined
): any[] => {
  const result: any[] = [];
  const attributes: Record<string, any> = {};
  const relationships: Record<string, any> = {};

  const generalSection = sections.find((s) => s.id === "general");
  if (generalSection?.selected) {
    for (const option of generalSection.options?.filter((o) => o.selected) ??
      []) {
      // CreateBlueprintFromProject "General" items
      if (option.id === "executive-summary") {
        attributes["executive-summary"] = project?.executiveSummary ?? "";
      } else if (option.id === "solution-summary") {
        attributes["solution-summary"] = project?.solutionSummary ?? "";
      } else if (option.id === "rate-table" && project?.rateTableId != null) {
        relationships["rate-table"] = {
          data: { id: Number(project.rateTableId), type: "rate-tables" },
        };
      } else if (
        option.id === "payment-term" &&
        project?.paymentTermId != null
      ) {
        relationships["payment-term"] = {
          data: { id: Number(project.paymentTermId), type: "payment-terms" },
        };
      }
      // DuplicateProject "General" items
      else if (option.id === "customer-summary") {
        attributes["executive-summary"] = project?.executiveSummary ?? "";
        attributes["solution-summary"] = project?.solutionSummary ?? "";
      } else if (option.id === "contacts") {
        result.push(...(project?.projectContacts ?? []));
      } else if (option.id === "terms-and-conditions") {
        result.push(...(project?.projectConditions ?? []));
      } else if (option.id === "teammates") {
        result.push(...(project?.projectCollaborators ?? []));
      } else if (option.id === "attachments") {
        result.push(...(project?.projectAttachments ?? []));
      } else if (option.id === "notes") {
        result.push(...(project?.projectNotes ?? []));
      }
    }
  }

  const pricingSection = sections.find((s) => s.id === "pricing");
  if (pricingSection?.selected) {
    for (const option of pricingSection.options?.filter((o) => o.selected) ??
      []) {
      if (option.id === "one-time-revenue-adjustments") {
        attributes["ps-revenue-adjustment"] =
          project?.psRevenueAdjustment ?? "";
      } else if (option.id === "recurring-revenue-adjustments") {
        attributes["ms-revenue-adjustment"] =
          project?.msRevenueAdjustment ?? "";
      } else if (option.id === "payment-credits") {
        result.push(...(project?.projectCredits ?? []));
      }
    }
  }

  if (
    Object.keys(attributes).length > 0 ||
    Object.keys(relationships).length > 0
  ) {
    const projectResource: any = { type: "projects", id: Number(project?.id) };
    if (Object.keys(attributes).length > 0)
      projectResource.attributes = attributes;
    if (Object.keys(relationships).length > 0)
      projectResource.relationships = relationships;
    result.unshift(projectResource);
  }

  for (const section of sections.filter(
    (s) => s.id !== "general" && s.id !== "pricing"
  )) {
    if (section.selected) {
      (section.options ?? [])
        .filter((o) => o.selected)
        .forEach((o) => result.push((o as any).rawResource));
    }
  }

  return result;
};
